import { isNavigationAsset, } from "./asset.js";
import { areaSurface } from "./area-geometry.js";
import { funnelPath } from "./funnel.js";
import { isNavigationPointCovered, locationNearestHeight, navigationCandidates, } from "./locate.js";
import { findNavigationRoute } from "./search.js";
/** Abtastschritt (Radar-Einheiten) für den Wechsel gestrichelt/durchgezogen. */
const COVER_SAMPLE_STEP = 4;
/** Kürzester Lauf (Radar-Einheiten, ~200 HU), der den Zustand wechseln darf. */
const MIN_COVERAGE_RUN = 40;
function toPoint(vertex) {
    return {
        x: Math.round(vertex.x * 1000) / 1000,
        y: Math.round(vertex.y * 1000) / 1000,
        z: Math.round(vertex.z * 10) / 10,
    };
}
function resolveLocation(asset, point, previous) {
    const candidates = navigationCandidates(asset, point);
    if (candidates.length === 0)
        return null;
    if (point.z != null)
        return locationNearestHeight(candidates, point.z);
    if (candidates.length === 1)
        return candidates[0];
    if (previous) {
        let best = null;
        for (const candidate of candidates) {
            const route = findNavigationRoute(asset, previous, candidate);
            if (route && (!best || route.seconds < best.seconds))
                best = { location: candidate, seconds: route.seconds };
        }
        if (best)
            return best.location;
    }
    // Ohne Anhaltspunkt: die flächenstärkste Ebene (meist der Boden).
    return candidates.reduce((largest, candidate) => areaSurface(asset.areas[candidate.areaId]) >
        areaSurface(asset.areas[largest.areaId])
        ? candidate
        : largest);
}
/** Fügt Punkte ein, wo der Weg unter eine Ebene taucht oder wieder auftaucht. */
function withCoverage(asset, path) {
    const points = [];
    const covered = [];
    const add = (point) => {
        points.push(point);
        covered.push(isNavigationPointCovered(asset, point));
    };
    add(path[0]);
    for (let index = 1; index < path.length; index += 1) {
        const from = path[index - 1];
        const to = path[index];
        const length = Math.hypot(to.x - from.x, to.y - from.y);
        const samples = Math.floor(length / COVER_SAMPLE_STEP);
        for (let sample = 1; sample < samples; sample += 1) {
            const t = sample / samples;
            const probe = {
                x: from.x + (to.x - from.x) * t,
                y: from.y + (to.y - from.y) * t,
                z: from.z + (to.z - from.z) * t,
            };
            if (isNavigationPointCovered(asset, probe) !== covered.at(-1))
                add(probe);
        }
        add(to);
    }
    smoothCoverage(points, covered);
    return { points, covered };
}
/**
 * Kurze Wechsel zwischen offen und abgedeckt (Lücken zwischen den Areas der
 * oberen Ebene) würden die Linie zerhacken: Läufe unter
 * `MIN_COVERAGE_RUN` mit gleichem Zustand auf beiden Seiten werden
 * angeglichen. Erst schließen sich offene Lücken, dann verschwinden
 * vereinzelte abgedeckte Inseln – sonst löscht eine Insel die Lücke daneben
 * nicht, sondern verlängert sie.
 */
function smoothCoverage(points, covered) {
    fillShortRuns(points, covered, false);
    fillShortRuns(points, covered, true);
}
function fillShortRuns(points, covered, state) {
    let start = 0;
    while (start < covered.length) {
        let end = start;
        while (end + 1 < covered.length && covered[end + 1] === covered[start])
            end += 1;
        const before = covered[start - 1];
        const after = covered[end + 1];
        if (covered[start] === state && before !== undefined && before === after) {
            let length = 0;
            for (let index = start; index <= end + 1; index += 1)
                length += Math.hypot(points[index].x - points[index - 1].x, points[index].y - points[index - 1].y);
            if (length < MIN_COVERAGE_RUN)
                for (let index = start; index <= end; index += 1)
                    covered[index] = before;
        }
        start = end + 1;
    }
}
/** Entfernt Zwischenpunkte, die weder Richtung, Aktion noch Abdeckung ändern. */
function simplify(points, covered) {
    const keptPoints = [points[0]];
    const keptCovered = [covered[0]];
    for (let index = 1; index < points.length - 1; index += 1) {
        const before = keptPoints.at(-1);
        const point = points[index];
        const after = points[index + 1];
        const turn = (point.x - before.x) * (after.y - before.y) -
            (point.y - before.y) * (after.x - before.x);
        const span = Math.hypot(after.x - before.x, after.y - before.y) || 1;
        if (!point.action &&
            covered[index] === keptCovered.at(-1) &&
            covered[index] === covered[index + 1] &&
            Math.abs(turn) / span < 0.05)
            continue;
        keptPoints.push(point);
        keptCovered.push(covered[index]);
    }
    keptPoints.push(points.at(-1));
    keptCovered.push(covered.at(-1));
    return { points: keptPoints, covered: keptCovered };
}
function blockedSegment(from) {
    return {
        points: [from],
        reachable: false,
        actions: [],
        covered: [false],
        seconds: null,
    };
}
function segmentBetween(asset, start, goal) {
    const route = findNavigationRoute(asset, start, goal);
    if (!route)
        return null;
    const path = funnelPath(asset, start, route.steps, goal);
    const { points, covered } = simplify(...unpack(withCoverage(asset, path)));
    const actions = [];
    points.forEach((point, pointIndex) => {
        if (point.action)
            actions.push({ pointIndex, kind: point.action });
    });
    return {
        points: points.map(toPoint),
        reachable: true,
        actions,
        covered,
        seconds: Math.round(route.seconds * 100) / 100,
    };
}
const unpack = (value) => [value.points, value.covered];
/** Hängt Abschnitte zu einem Gesamtweg aneinander (Startpunkt nur einmal). */
function joinSegments(segments, reachable, blockedAt) {
    const points = [];
    const covered = [];
    const actions = [];
    segments.forEach((segment, index) => {
        const skip = index === 0 ? 0 : 1;
        const offset = points.length - skip;
        for (const action of segment.actions)
            if (action.pointIndex >= skip)
                actions.push({ ...action, pointIndex: action.pointIndex + offset });
        points.push(...segment.points.slice(skip));
        covered.push(...segment.covered.slice(skip));
    });
    return { points, covered, actions, segments, reachable, blockedAt };
}
export function computeWalkingPathResult(data, waypoints) {
    if (waypoints.length < 2 || !data || !isNavigationAsset(data))
        return {
            ...joinSegments(waypoints.slice(0, -1).map((point, index) => ({
                points: [point, waypoints[index + 1]],
                reachable: true,
                actions: [],
                covered: [false, false],
                seconds: null,
            })), true, null),
            points: waypoints,
            covered: waypoints.map(() => false),
        };
    const segments = [];
    let previous = resolveLocation(data, waypoints[0], null);
    if (!previous)
        return joinSegments([blockedSegment(waypoints[0])], false, 0);
    let lastPoint = toPoint(previous);
    for (let index = 1; index < waypoints.length; index += 1) {
        const next = resolveLocation(data, waypoints[index], previous);
        const segment = next ? segmentBetween(data, previous, next) : null;
        if (!next || !segment) {
            segments.push(blockedSegment(lastPoint));
            return joinSegments(segments, false, index);
        }
        segments.push(segment);
        lastPoint = segment.points.at(-1);
        previous = next;
    }
    return joinSegments(segments, true, null);
}
export function computeWalkingPath(data, waypoints) {
    return computeWalkingPathResult(data, waypoints).points;
}
/**
 * Derselbe Laufweg wie `computeWalkingPath`, aber je Wegpunkt-Abschnitt –
 * die Quickview läuft damit bei der Wiedergabe von Schritt N nach N+1.
 */
export function computeWalkingPathSegments(data, waypoints) {
    return computeWalkingPathResult(data, waypoints).segments.map((segment) => segment.points);
}
/** Ebenen unter einem Radarpunkt, oberste zuerst (für den Ebenenwechsel). */
export function navigationFloorsAt(data, point) {
    if (!data || !isNavigationAsset(data))
        return [];
    return navigationCandidates(data, point).map((candidate) => ({
        ...point,
        ...toPoint(candidate),
    }));
}
/**
 * Legt einen Routenpunkt auf die Navmesh: x/y wird nur bei einem Klick neben
 * die Fläche verschoben, `z` bestimmt die Ebene. Ohne gespeicherte Höhe
 * entscheidet der schnellste Weg vom vorherigen Punkt.
 */
export function inferNavigationPoint(data, point, previous) {
    if (!data || !isNavigationAsset(data))
        return point;
    const previousLocation = previous
        ? resolveLocation(data, previous, null)
        : null;
    const location = resolveLocation(data, point, previousLocation);
    if (!location)
        return point;
    return { ...point, ...toPoint(location) };
}
/** Wechselt bei gestapelten Ebenen zur nächsthöheren (+1) oder -tieferen (−1). */
export function cycleNavigationFloor(data, point, direction) {
    const floors = navigationFloorsAt(data, point);
    if (floors.length < 2)
        return null;
    const current = floors.reduce((best, floor, index) => Math.abs((floor.z ?? 0) - (point.z ?? 0)) <
        Math.abs((floors[best].z ?? 0) - (point.z ?? 0))
        ? index
        : best, 0);
    // `floors` ist von oben nach unten sortiert.
    const next = current - direction;
    return floors[next] ?? null;
}
export function isNavigationPointPathable(data, point) {
    if (!data || !isNavigationAsset(data))
        return true;
    return navigationCandidates(data, point).length > 0;
}
